import { createReducer } from "@reduxjs/toolkit";
import { EntryRightDelegeeDetails, GetDelegatedEntryRightsQuery } from "../../api/entryRights.api";
import { DelegatedEntryRight, FlatDelegee, PedestrianRightsMyFleetState } from "../../models/delegations/DelegatedEntryRight";
import { ErrorDetailsModel } from "../../models/ErrorDetailsModel";
import { LocatedParkingProduct } from "../../models/LocatedParkingProduct";
import { LocatedParkingProductModel, ParkingProductModel, ParkingProductType } from "../../models/ParkingProductModel";
import * as entryRightsActions from "../actions/entryRights.actions";
import * as parkingProductsThunks from "../actions/parkingProducts.thunks";
import * as entryRightOptimisticUpdates from "../helpers/entry-right-optimistic-updates";
import { DataType } from "./parking-products";
import { ForFleetManager } from "../../models/ForFleetManager";
import { WithFiltersAndPaging } from "../../models/filters/WithFiltersAndPaging";

export type EntryRightsPagedData<T> = {
    totalPages: number;
    totalRecords: number;
    pages: {
        [page: number]: {
            loading: boolean;
            data?: T[] | null;
            error?: ErrorDetailsModel | null;
            query: GetDelegatedEntryRightsQuery;
        };
    };
}

export type MyFleetFlatDelegeeData = {
    totalPages: number;
    totalRecords: number;
    totalUniqueDelegees: number;
    pages: {
        [page: number]: {
            loading: boolean;
            data?: FlatDelegee[] | null;
            error?: ErrorDetailsModel | null;
            query: WithFiltersAndPaging<ForFleetManager>;
        };
    };
};

type MyFleetOverviewState = {
    pedestrians: MyFleetFlatDelegeeData;
    parkingRights: MyFleetFlatDelegeeData;
};

type DelegatedEntryRightsPagedData = EntryRightsPagedData<DelegatedEntryRight>;

export type EntryRightsState = {
    loading: boolean;
    myProducts: {
        pedestrian: DelegatedEntryRightsPagedData;
        parking: DelegatedEntryRightsPagedData;
    };
    myFleet: PedestrianRightsMyFleetState;
    myFleetOverview: MyFleetOverviewState
    parkingProducts: ParkingProductModel[];
    error?: ErrorDetailsModel;
    delegeeDetails: DataType<EntryRightDelegeeDetails>;
    updateState: {
        loading: boolean;
        error?: ErrorDetailsModel;
    },
    validateDelegees: {
        loading: boolean;
        success?: boolean;
        fileReference?: string;
    },
    bulkImportProgress: {
        progress: number;
        hasErrors: boolean;
        numberOfErrors: number;
    },
    isBulkImportCompleted: boolean;
    showRevokePedestriansWarning: boolean;
    showDeleteEntryRightDelegeesWarning: boolean;
    locatedParkingProducts: LocatedParkingProductModel[];
}

function getMyFleetDelegeeState(state: MyFleetOverviewState, productType: ParkingProductType) : MyFleetFlatDelegeeData {
    if (productType === ParkingProductType.PedestrianAccess) {
        return state.pedestrians;
    } else if (productType === ParkingProductType.ParkingRights) {
        return state.parkingRights;
    }
    throw new Error(`Unsupported product type: ${productType}`);
}

const initialState: EntryRightsState = {
    loading: false,
    error: undefined,
    myProducts: {
        pedestrian: { totalPages: 0, totalRecords: 0, pages: {} },
        parking: { totalPages: 0, totalRecords: 0, pages: {} }
    },
    parkingProducts: [],
    delegeeDetails: {
        loading: false,
        data: null,
        error: null,
    },
    updateState: {
        loading: false,
    },
    validateDelegees: {
        loading: false,
        success: undefined,
        fileReference: undefined,
    },
    bulkImportProgress: {
        progress: 0,
        hasErrors: false,
        numberOfErrors: 0,
    },
    isBulkImportCompleted: true,
    showRevokePedestriansWarning: false,
    showDeleteEntryRightDelegeesWarning: false,
    myFleet: {
        delegees: {
            totalPages: 0,
            totalRecords: 0,
            pages: [
                {
                    loading: false,
                    data: [],
                    error: null,
                }
            ],
        },
        pageNumber: 0,
    },
    myFleetOverview: {
        pedestrians: {
            totalPages: 0,
            totalRecords: 0,
            totalUniqueDelegees: 0,
            pages: {}
        },
        parkingRights: {
            totalPages: 0,
            totalRecords: 0,
            totalUniqueDelegees: 0,
            pages: {}
        },
    },
    locatedParkingProducts: [],
}

function getDelegatedEntryRightsPagedData(state: EntryRightsState, productType: ParkingProductType): DelegatedEntryRightsPagedData {
    if (productType === ParkingProductType.PedestrianAccess) {
        return state.myProducts.pedestrian;
    } else if (productType === ParkingProductType.ParkingRights) {
        return state.myProducts.parking;
    }
    throw new Error(`Unsupported product type: ${productType}`);
}

export const entryRightsReducer = createReducer(initialState, (builder) => {
    builder
        .addCase(entryRightsActions.getDelegatedEntryRightsThunk.pending, (state, action) => {
            getDelegatedEntryRightsPagedData(state, action.meta.arg.productType!).pages[action.meta.arg.page] = {
                loading: true,
                query: action.meta.arg,
            };
        })
        .addCase(entryRightsActions.getDelegatedEntryRightsThunk.fulfilled, (state, action) => {
            const entryRights = getDelegatedEntryRightsPagedData(state, action.meta.arg.productType!);
            entryRights.totalPages = action.payload.totalPages;
            entryRights.totalRecords = action.payload.totalRecords;
            entryRights.pages[action.payload.query.page] = {
                loading: false,
                data: action.payload.data,
                query: action.payload.query
            };

            updateLocationNameForMyProducts(state, entryRights);
        })
        .addCase(entryRightsActions.getDelegatedEntryRightsThunk.rejected, (state, action) => {
            getDelegatedEntryRightsPagedData(state, action.meta.arg.productType!).pages[action.meta.arg.page] = {
                loading: false,
                query: action.meta.arg
            };
        })
        .addCase(entryRightsActions.inviteActionThunk.pending, (state, _) => {
            state.loading = true;
        })
        .addCase(entryRightsActions.inviteActionThunk.fulfilled, (state, action) => {
            if (!action.payload.ok) {
                state.error = action.payload.error;
                state.loading = false;
                return;
            }
            const { value } = action.payload;
            entryRightOptimisticUpdates.applyInviteEntryRightsToMyProducts(state.locatedParkingProducts, state.myProducts.parking, action.meta.arg, value);
            entryRightOptimisticUpdates.applyInviteEntryRightsToMyProducts(state.locatedParkingProducts, state.myProducts.pedestrian, action.meta.arg, value);
            entryRightOptimisticUpdates.applyOptimisticInviteOnMyFleet(state, action.meta.arg, value);
            entryRightOptimisticUpdates.applyOptimisticInviteOnMyFleetOverview(state, action.meta.arg, value);
            state.loading = false;
        })
        .addCase(entryRightsActions.inviteActionThunk.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = false;
        })
        .addCase(entryRightsActions.clearDelegeeDetailsAction, (state, _) => {
            state.delegeeDetails = initialState.delegeeDetails;
        })
        .addCase(entryRightsActions.disposeErrorAction, (state, _) => {
            state.loading = initialState.loading;
            state.error = initialState.error;
            state.updateState.error = initialState.updateState.error;
        })
        .addCase(entryRightsActions.toggleShowRevokeEntryRightsAction, (state) => {
            state.showRevokePedestriansWarning = !state.showRevokePedestriansWarning;
        })
        .addCase(entryRightsActions.revokeEntryRightsRequestAction, (state) => {
            state.loading = true;
        })
        .addCase(entryRightsActions.revokeEntryRightsSuccessAction, (state, action) => {
            state.myProducts.pedestrian = entryRightOptimisticUpdates.reduceRevokeEntryRightsMyProducts(state.myProducts.pedestrian, action.payload.model);
            state.myProducts.parking = entryRightOptimisticUpdates.reduceRevokeEntryRightsMyProducts(state.myProducts.parking, action.payload.model);
            entryRightOptimisticUpdates.applyOptimisticRevokeOnMyFleet(state, action.payload.model);
            entryRightOptimisticUpdates.applyRevokeResponseOnMyFleetOverview(state, action.payload.model, action.payload.response);
            state.loading = false;
        })
        .addCase(entryRightsActions.revokeEntryRightsFailureAction, (state) => {
            state.loading = false;
        })
        .addCase(entryRightsActions.resendInviteActionThunk.pending, (state) => {
            state.loading = true;
        })
        .addCase(entryRightsActions.resendInviteActionThunk.fulfilled, (state) => {
            state.loading = false;
        })
        .addCase(entryRightsActions.resendInviteActionThunk.rejected, (state) => {
            state.loading = false;
        })
        .addCase(entryRightsActions.resendAllInvitesActionThunk.pending, (state) => {
            state.loading = true;
        })
        .addCase(entryRightsActions.resendAllInvitesActionThunk.fulfilled, (state) => {
            state.loading = false;
        })
        .addCase(entryRightsActions.resendAllInvitesActionThunk.rejected, (state) => {
            state.loading = false;
        })
        .addCase(entryRightsActions.toggleShowDeleteEntryRightsDelegeesAction, (state) => {
            state.showDeleteEntryRightDelegeesWarning = !state.showDeleteEntryRightDelegeesWarning;
        })
        .addCase(entryRightsActions.deleteEntryDelegeesRequestAction, (state) => {
            state.loading = true;
        })
        .addCase(entryRightsActions.deleteEntryDelegeesSuccessAction, (state, action) => {
            state.myProducts.pedestrian = entryRightOptimisticUpdates.reduceDeleteEntryRightsMyProducts(state.myProducts.pedestrian, action.payload.model!)
            state.myProducts.parking = entryRightOptimisticUpdates.reduceDeleteEntryRightsMyProducts(state.myProducts.parking, action.payload.model!)
            entryRightOptimisticUpdates.applyOptimisticDeleteMyFleet(state, action.payload.model!);
            entryRightOptimisticUpdates.applyOptimisticDeleteMyFleetOverview(state, action.payload.model!);
            state.loading = false;
        })
        .addCase(entryRightsActions.deleteEntryDelegeesFailureAction, (state) => {
            state.loading = false;
        })
        .addCase(entryRightsActions.getEntryRightDelegeesMyFleetRequestAction, (state, action) => {
            const pageNumber = action.payload!.pagination.pageNumber;
            state.myFleet.pageNumber = pageNumber;
            state.myFleet.delegees.pages = {
                ...state.myFleet.delegees.pages,
                [pageNumber]: {
                    loading: true,
                    data: [],
                    error: null
                }
            };
            state.myFleet.delegees.pages![pageNumber].data = []
        })
        .addCase(entryRightsActions.getEntryRightDelegeesMyFleetSuccessAction, (state, action) => {
            state.myFleet.delegees.pages![state.myFleet.pageNumber].loading = false;
            state.myFleet.delegees.pages![state.myFleet.pageNumber].data = action.payload.delegees;
            state.myFleet.delegees.totalPages = action.payload.pages;
            state.myFleet.delegees.totalRecords = action.payload.totalRecords;
        })
        .addCase(entryRightsActions.getEntryRightDelegeesMyFleetFailureAction, (state, action) => {
            state.error = action.payload.response?.data as ErrorDetailsModel;
            state.myFleet.delegees.pages![state.myFleet.pageNumber].loading = false;
            state.myFleet.delegees.pages![state.myFleet.pageNumber].data = [];
            state.myFleet.delegees.pages![state.myFleet.pageNumber].error = action.payload.response?.data as ErrorDetailsModel;
        })
        .addCase(entryRightsActions.getEntryRightDelegeesMyFleetOverviewThunk.pending, (state, action) => {
            const pageNumber = action.meta.arg.request.pagination.pageNumber;
            const myFleetData = getMyFleetDelegeeState(state.myFleetOverview, action.meta.arg.productContractTypeFilter.productType!);
            myFleetData.pages[pageNumber] = {
                loading: true,
                data: [],
                query: action.meta.arg.request,
            };
        })
        .addCase(entryRightsActions.getEntryRightDelegeesMyFleetOverviewThunk.fulfilled, (state, action) => {
            const pageNumber = action.meta.arg.request.pagination.pageNumber;
            const myFleetData = getMyFleetDelegeeState(state.myFleetOverview, action.meta.arg.productContractTypeFilter.productType!);
            myFleetData.totalPages = action.payload.pages;
            myFleetData.totalRecords = action.payload.totalRecords;
            myFleetData.totalUniqueDelegees = action.payload.totalUniqueDelegees;
            myFleetData.pages[pageNumber] = {
                loading: false,
                data: action.payload.delegees,
                query: action.meta.arg.request,
            };
        })
        .addCase(entryRightsActions.getEntryRightDelegeesMyFleetOverviewThunk.rejected, (state, action) => {
            const pageNumber = action.meta.arg.request.pagination.pageNumber;
            const myFleetData = getMyFleetDelegeeState(state.myFleetOverview, action.meta.arg.productContractTypeFilter.productType!);
            myFleetData.pages[pageNumber] = {
                loading: false,
                data: [],
                error: action.payload ?? null,
                query: action.meta.arg.request,
            };
        })
        .addCase(entryRightsActions.getEntryRightDelegeeDetailsActionThunk.pending, state => {
            state.delegeeDetails.loading = true;
        })
        .addCase(entryRightsActions.getEntryRightDelegeeDetailsActionThunk.fulfilled, (state, p) => {
            state.delegeeDetails.loading = false;
            state.delegeeDetails.data = p.payload;
        })
        .addCase(entryRightsActions.getEntryRightDelegeeDetailsActionThunk.rejected, (state, p) => {
            state.delegeeDetails.loading = false;
        })
        .addCase(entryRightsActions.updateEntryRightDelegeeActionThunk.pending, (state, action) => {
            state.updateState.loading = true;
        })
        .addCase(entryRightsActions.updateEntryRightDelegeeActionThunk.fulfilled, (state, action) => {
            state.updateState.loading = false;
            if (!action.payload.ok) {
                state.updateState.error = action.payload.error;
                return;
            }
            const { value } = action.payload;
            entryRightOptimisticUpdates.applyEntryRightsUpdateOnDraftMyProducts(state.parkingProducts, state.myProducts.parking, action.meta.arg.model, value);
            entryRightOptimisticUpdates.applyEntryRightsUpdateOnDraftMyProducts(state.parkingProducts, state.myProducts.pedestrian, action.meta.arg.model, value);
            entryRightOptimisticUpdates.applyOptimisticUpdateOnMyFleet(state, action.meta.arg.model, value);
            entryRightOptimisticUpdates.applyOptimisticUpdateOnMyFleetOverview(state, action.meta.arg.model, value);
        })
        .addCase(entryRightsActions.updateEntryRightDelegeeActionThunk.rejected, (state, action) => {
            state.updateState.loading = false;
            state.updateState.error = action.payload;
        })
        .addCase(parkingProductsThunks.getLocatedParkingProductsThunk.fulfilled, (state, action) => {
            state.locatedParkingProducts = mapToLocatedParkingProducts(action.payload);
        })
        .addCase(entryRightsActions.startBulkInvitationsThunk.pending, (state) => {
            state.loading = true;
        })
        .addCase(entryRightsActions.startBulkInvitationsThunk.fulfilled, (state) => {
            state.loading = false;
        })
        .addCase(entryRightsActions.startBulkInvitationsThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(entryRightsActions.validateDelegeesThunk.pending, (state) => {
            state.validateDelegees.loading = true;
            state.validateDelegees.success = undefined;
        })
        .addCase(entryRightsActions.validateDelegeesThunk.fulfilled, (state, action) => {
            state.validateDelegees.loading = false;
            state.validateDelegees.success = true;
            state.validateDelegees.fileReference = action.payload.fileReference;
        })
        .addCase(entryRightsActions.validateDelegeesThunk.rejected, (state) => {
            state.validateDelegees.loading = false;
            state.validateDelegees.success = false;
        })
        .addCase(entryRightsActions.getBulkImportProgressThunk.fulfilled, (state, action) => {
            state.bulkImportProgress = action.payload;
        })
        .addCase(entryRightsActions.getBulkImportStatusActions.pending, (state) => {
            state.loading = true;
        })
        .addCase(entryRightsActions.getBulkImportStatusActions.fulfilled, (state, action) => {
            state.loading = false;
            state.isBulkImportCompleted = action.payload.processCompleted;
        })
        .addCase(entryRightsActions.getBulkImportStatusActions.rejected, (state, action) => {
            state.loading = false;
            state.isBulkImportCompleted = true;
            state.error = action.payload;
        })
        .addCase(entryRightsActions.resetBulkImportProgressAction, (state) => {
            state.bulkImportProgress = initialState.bulkImportProgress;
            state.validateDelegees = initialState.validateDelegees;
        })
        .addCase(parkingProductsThunks.updateDelegationThunk.fulfilled, (state, { payload }) => {
            if (!payload.ok) {
                state.updateState.error = payload.error;
                state.updateState.loading = false;
                return;
            }
            const { response, original } = payload.value;
            if (original) {
                const firstPaProductAdded = response.assignedProducts.some(p => p.productType === ParkingProductType.PedestrianAccess) &&
                    !original.products.some(p => p.productType === ParkingProductType.PedestrianAccess)
                if (firstPaProductAdded) {
                    state.myFleet.delegees.totalRecords += 1;
                }

                const lastPaProductRemoved = !response.assignedProducts.some(p => p.productType === ParkingProductType.PedestrianAccess) &&
                    original.products.some(p => p.productType === ParkingProductType.PedestrianAccess)
                if (lastPaProductRemoved) {
                    state.myFleet.delegees.totalRecords -= 1;
                }

                // don't allow negative totals
                state.myFleet.delegees.totalRecords = Math.max(0, state.myFleet.delegees.totalRecords);
            }
        })
        .addCase(parkingProductsThunks.getParkingProducts.fulfilled, (state, action) => {
            state.parkingProducts = action.payload;
        })
        .addCase(entryRightsActions.getTotalRecords.fulfilled, (state, action) => {
            const entryRights = getDelegatedEntryRightsPagedData(state, action.meta.arg.productType!);
            entryRights.totalRecords = action.payload.totalRecords;
        });
});

export const mapToLocatedParkingProducts = (data: LocatedParkingProduct[]): LocatedParkingProductModel[] => {
    const parkingProducts = Array.isArray(data) && data?.flatMap((x) => x.parkingProducts);

    if (!parkingProducts || parkingProducts === undefined || parkingProducts.length === 0) {
        return [];
    }

    return parkingProducts?.map((x: ParkingProductModel): LocatedParkingProductModel => {
        return {
            id: x.id,
            pmcId: x.pmcId,
            pmcName: x.pmcName,
            location: x.location,
            countryCode: x.countryCode,
            placeId: x.placeId,
            contractStartDate: x.contractStartDate,
            contractType: x.contractType,
            productType: x.productType,
        };
    });
}

export function updateLocationNameForMyProducts(state: EntryRightsState, entryRights: DelegatedEntryRightsPagedData) {
    for (const pageNumber in entryRights.pages) {
        const page = entryRights.pages[pageNumber];
        if (!page.data) {
            continue;
        }
        const { query, data } = page;

        for (const delegatedEntryRight of data) {
            const locationName =
                state.locatedParkingProducts.find((p) =>
                    p.placeId === delegatedEntryRight.product.placeId &&
                    (!query.contractType || query.contractType === p.contractType) &&
                    (!query.productType || query.productType === p.productType)
                )?.location ?? "";

            delegatedEntryRight.product.locationName = locationName;
        }
    }
}
